<template>
	<div
		class="pronto-radio-select"
		:class="{ disabled, 'hide-icons': hideIcons }"
	>
		<div class="pronto-radio-select-options">
			<div
				class="pronto-radio-select-option"
				v-for="option in options"
				:key="option.value"
				@click="onOptionToggle(option)"
				:class="{ selected: option.selected }"
				:data-value="option.value"
			>
				<div class="pronto-radio-select-icons">
					<CircleCheckIcon
						v-if="option.selected && !hideIcons"
						type="solid"
						class="pronto-radio-select-checked"
					/>
					<CircleIcon
						v-if="!option.selected && !disabled && !hideIcons"
						type="regular"
						class="pronto-radio-select-unchecked"
					/>
				</div>
				<div class="pronto-radio-select-option-content">
					<slot name="option" :option="option">
						<span v-text="option.label" />
					</slot>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed } from 'vue'
import { CircleCheckIcon, CircleIcon } from '@/icons'

const emit = defineEmits(['toggle'])

const model = defineModel()
const props = defineProps({
	multiple: {
		type: Boolean,
		default: false,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	hideIcons: {
		type: Boolean,
		default: false,
	},
	// options should adhere to the following format:
	// [{ value: string, label: string }, ...]
	options: {
		type: Array,
		required: true,
	},
})

const options = computed(() => {
	return props.options.map((option) => ({
		...option,
		selected: props.multiple
			? model.value?.includes(option.value) ?? false
			: model.value === option.value,
	}))
})

const onOptionToggle = ({ selected, ...option }) => {
	if (props.disabled) return
	const prevValue = model.value
	const isSelected = !selected
	let newValue = null
	if (props.multiple) {
		if (!Array.isArray(model.value)) model.value = []
		if (isSelected) {
			// need to add the option to the selected options
			newValue = [...model.value, option.value]
		} else {
			// need to remove the option from the selected options
			newValue = model.value.filter((id) => id !== option.value)
		}
	} else {
		newValue = isSelected ? option.value : null
	}
	model.value = newValue
	emit('toggle', option, isSelected)
	emit('change', newValue, prevValue)
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.pronto-radio-select {
	@include flex-column(8px);
	width: 100%;

	&.disabled {
		.pronto-radio-select-options {
			.pronto-radio-select-option {
				cursor: default;
			}
		}
	}

	&.hide-icons {
		.pronto-radio-select-option {
			.pronto-radio-select-icons {
				display: none;
			}
		}
	}

	.pronto-radio-select-options {
		@include flex-column(16px);
		.pronto-radio-select-option {
			cursor: pointer;
			@include flex(8px);
			align-items: flex-start;
			&.selected {
				.pronto-icon {
					color: var(--teal-300) !important;
				}
			}
			.pronto-radio-select-icons {
				width: 24px;
				height: 24px;
				.pronto-icon {
					font-size: 24px;
					color: var(--neutral-400);
				}
			}
			.pronto-radio-select-option-content {
				width: 100%;
			}
			:deep(.pronto-progress) {
				background-color: var(--neutral-200);
			}
		}
	}
}
</style>
